
import { Avatar, Chip, FormControl, Grid, InputLabel, MenuItem, Select, Stack, Tooltip, Typography } from '@mui/material'
import { GoogleMap, LoadScript, MarkerF } from '@react-google-maps/api'
import PublicIcon from '@mui/icons-material/Public'
import FlagIcon from '@mui/icons-material/Flag'
import LocationCityIcon from '@mui/icons-material/LocationCity'
import MapIcon from '@mui/icons-material/Map'
import React, { useCallback } from 'react'
import BadgeIcon from '@mui/icons-material/Badge'
import CakeIcon from '@mui/icons-material/Cake'
import PsychologyIcon from '@mui/icons-material/Psychology'
import CheckroomIcon from '@mui/icons-material/Checkroom'
import BiotechIcon from '@mui/icons-material/Biotech'
import WcIcon from '@mui/icons-material/Wc'
import PersonIcon from '@mui/icons-material/Person'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import EmojiSymbolsIcon from '@mui/icons-material/EmojiSymbols'
import WbSunnyIcon from '@mui/icons-material/WbSunny'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import PlaceIcon from '@mui/icons-material/Place'
import WarningIcon from '@mui/icons-material/Warning'
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred'
import SecurityIcon from '@mui/icons-material/Security'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { isNumber } from 'lodash'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import PeopleIcon from '@mui/icons-material/People'
import TextFieldsIcon from '@mui/icons-material/TextFields'
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera'
import FlipCameraAndroidIcon from '@mui/icons-material/FlipCameraAndroid'
import ThermostatIcon from '@mui/icons-material/Thermostat'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import MyLocationIcon from '@mui/icons-material/MyLocation'
import Explore from '@mui/icons-material/Explore'
import { SearchResult } from '../../store/searcher/reducer'
import { prepareImageInsizeResults } from '../../store/searcher/service'

type Props = {
  apiData: SearchResult
}

export interface PersonProfile {
  accessories?: string;
  ageRange?: string;
  behaviorNotes?: string;
  clothing?: string;
  ethnicity?: string;
  gender?: string;
  identifiedAs?: string;
  riskIndicators?: string;
  tattoos?: string;
}

interface PeopleComboboxProps {
  people?: PersonProfile[];
}

const PeopleCombobox: React.FC<PeopleComboboxProps> = ({ people = [] }) => {
  const [selected, setSelected] = React.useState(0)

  if (!people.length) return (
    <Stack direction="column" spacing={1}>

      <Typography variant="h6">People:</Typography>
      <Chip label={"Not detected"} size="small" />
    </Stack>)

  const person = people[selected] ?? {}

  return (
    <Stack spacing={2} sx={{ width: 1 }}>
      <FormControl size="small" fullWidth>
        <InputLabel id="person-select-label">Profiles</InputLabel>
        <Select
          labelId="person-select-label"
          value={selected}
          label="Person"
          onChange={(e) => setSelected(Number(e.target.value))}
        >
          {people.map((_, idx) => (
            <MenuItem value={idx} key={idx}>
              Person {idx + 1}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Stack spacing={1}>
        {person.accessories && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Accessories">
            <BadgeIcon color="primary" />
            </Tooltip>
            <Typography>{person.accessories}</Typography>
          </Stack>
        )}
        {person.ageRange && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Age">
            <CakeIcon color="primary" />
            </Tooltip>
            <Typography>{person.ageRange}</Typography>
          </Stack>
        )}
        {person.behaviorNotes && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Behavior notes">
            <PsychologyIcon color="primary" />
            </Tooltip>
            <Typography>{person.behaviorNotes}</Typography>
          </Stack>
        )}
        {person.clothing && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Clothing">
            <CheckroomIcon color="primary" />
            </Tooltip>
            <Typography>{person.clothing}</Typography>
          </Stack>
        )}
        {person.ethnicity && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Ethnicity">
            <BiotechIcon color="primary" />
            </Tooltip>
            <Typography>{person.ethnicity}</Typography>
          </Stack>
        )}
        {person.gender && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Gender">
            <WcIcon color="primary" />
            </Tooltip>
            <Typography>{person.gender}</Typography>
          </Stack>
        )}
        {person.identifiedAs && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Identified as">
            <PersonIcon color="primary" />
            </Tooltip>
            <Typography>{person.identifiedAs}</Typography>
          </Stack>
        )}
        {person.riskIndicators && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Risk indicators">
            <WarningAmberIcon color="error" />
            </Tooltip>
            <Typography>{person.riskIndicators}</Typography>
          </Stack>
        )}
        {person.tattoos && (
          <Stack direction="row" spacing={1} alignItems="center">
             <Tooltip title="Tattoos">
            <EmojiSymbolsIcon color="primary" />
            </Tooltip>
            <Typography>{person.tattoos}</Typography>
          </Stack>
        )}
      </Stack>
    </Stack>
  )
}

type OutputChipProps = {
  array?: string[];
};

const OutputChip: React.FC<OutputChipProps> = ({ array = [] }) => {
  const maxLength = 20

  return (
    <Stack direction="column" spacing={1}>
      {array.map((el) => {
        const isLong = el.length > maxLength
        const label = isLong ? `${el.slice(0, maxLength)}...` : el

        return (
          <Tooltip key={el} title={isLong ? el : ''} arrow>
            <Chip
              label={label}
              size="small"
              color="primary"
              sx={{ cursor: isLong ? 'pointer' : 'default' }}
            />
          </Tooltip>
        )
      })}
    </Stack>
  )
}

export function ImageInsizeCommon(props: Props) {

  const result = prepareImageInsizeResults(props.apiData)
    const fromSource = JSON.parse(JSON.stringify(props.apiData))
  const sourceName = fromSource.apiDataEntities
    && Array.isArray(fromSource.apiDataEntities)
    && fromSource.apiDataEntities[0]
    && fromSource.apiDataEntities[0].from ? fromSource.apiDataEntities[0].from : "No data"

  const picture = result.criteria ? result.criteria : ""
  const profile = result.data && Array.isArray(result.data) ? result.data[0] : null

  const [map, setMap] = React.useState(null)

  const onLoad = useCallback((map) => {
    setMap(map)
  }, [])

  const lat = parseFloat(profile?.location?.latitude) ? parseFloat(profile?.location?.latitude) : profile?.location?.coordinates?.lat
  const lng = parseFloat(profile?.location?.longitude) ? parseFloat(profile?.location?.latitude) : profile?.location?.coordinates?.lon

  const zoom = 15

  const containerStyle = {
    width: 450,
    height: 300,
  }

  const isEmpty = (value: unknown): boolean => {
    if (value == null) return true

    if (typeof value === 'string') return value.trim() === ''

    if (Array.isArray(value)) return value.length === 0 || value.every(isEmpty)

    if (typeof value === 'object') {
      const vals = Object.values(value as Record<string, unknown>)
      return vals.length === 0 || vals.every(isEmpty)
    }

    return false
  }

  return (
    <>
      <Grid container mb={1} spacing={4}>
        <Grid item xs={4} sx={{
          border: '0px solid #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Avatar
            variant="rounded"
            src={picture}
            sx={{
              width: '100%',
              height: '100%',
              borderRadius: 4,
              objectFit: "cover",
            }}
          />
        </Grid>
        <Grid item xs={2} sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start' }}>
          <Stack spacing={1}>
            {profile?.location && (
              <Stack direction="column" spacing={1}>
                <Typography variant="h6">Location:</Typography>
              </Stack>
            )}
            {isEmpty(profile?.location) && (
              <Stack direction="column" spacing={1}>
                <Chip label={"Not detected"} size="small" />
              </Stack>
            )}
            {profile?.location?.continent && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Tooltip title="Continent">
                <PublicIcon color="primary" />
                </Tooltip>
                <Typography>{profile?.location?.continent}</Typography>
              </Stack>
            )}
            {profile?.location?.country && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Tooltip title="Country">
                <FlagIcon color="primary" />
                </Tooltip>
                <Typography>{profile?.location.country}</Typography>
              </Stack>
            )}
            {profile?.location?.city && (
              <Stack direction="row" alignItems="center">
                <Tooltip title="City">
                <LocationCityIcon color="primary" />
                </Tooltip>
                <Typography>{profile?.location.city}</Typography>
              </Stack>
            )}
            {profile?.location?.landmark && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Tooltip title="Landmark">
                <PlaceIcon color="primary" />
                </Tooltip>
                <Typography>{profile?.location.landmark}</Typography>
              </Stack>
            )}
            {profile?.location?.regionType && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Tooltip title="Region type">
                <MapIcon color="primary" />
                </Tooltip>
                <Typography>{profile?.location.regionType}</Typography>
              </Stack>
            )}
          </Stack>
        </Grid>
        <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {
            ((lat && lng) ?
              <LoadScript googleMapsApiKey={process.env.REACT_APP_FIREBASE_API_KEY || ""}>
                <GoogleMap
                  mapContainerStyle={containerStyle}
                  center={{ lat: lat, lng: lng }}
                  zoom={zoom}
                  options={
                    { minZoom: 1, maxZoom: zoom, clickableIcons: false, disableDefaultUI: true }}
                  onLoad={onLoad}
                >
                  <MarkerF
                    position={{
                      lat: lat,
                      lng: lng,
                    }}
                  />
                </GoogleMap>
              </LoadScript>
              :
              <Typography variant="bold" fontSize={14} sx={{ wordBreak: 'break-all' }}>No geolocation provided</Typography>
            )
          }
        </Grid>
      </Grid>

      <Grid container mb={1} spacing={4} sx={{ mt: 1 }}>
        <Grid item xs={3} sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start' }}>
          <Stack direction="column" spacing={1}>
            <PeopleCombobox people={profile?.people} />
          </Stack>
        </Grid>
        <Grid item xs={3} sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start' }}>
          <Stack direction="column" spacing={1}>
            
            {profile?.time && (
              <Stack direction="column" spacing={1}>
                <Typography variant="h6">Time:</Typography>
              </Stack>
            )}
            {isEmpty(profile?.time) && (
              <Stack direction="column" spacing={1}>
                <Chip label={"Not detected"} size="small" />
              </Stack>
            )}

            {profile?.time?.estimatedDate && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip title="Estimated date">
                <CalendarTodayIcon color="primary" />
                </Tooltip>
                <Typography>{profile?.time?.estimatedDate}</Typography>
              </Stack>
            )}
            {profile?.time?.estimatedTimeOfDay && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip title="Estimated time of day">
                <WbSunnyIcon color="primary" />
                </Tooltip>
                <Typography>{profile?.time?.estimatedTimeOfDay}</Typography>
              </Stack>
            )}
            {profile?.time?.season && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip title="Season">
                <ThermostatIcon color="primary" />
                </Tooltip>
                <Typography>{profile?.time?.season}</Typography>
              </Stack>
            )}
            
            {profile?.threatAssessment && (
              <Stack direction="column" spacing={1}>
                <Typography variant="h6">Threat assessment:</Typography>
              </Stack>
            )}
            {isEmpty(profile?.threatAssessment) && (
              <Stack direction="column" spacing={1}>
                <Chip label={"Not detected"} size="small" />
              </Stack>
            )}
            
            {profile?.threatAssessment?.Alert && profile?.threatAssessment?.Alert.length > 0 && (
              <Stack direction="row" spacing={1} alignItems="center">
                <WarningIcon color="warning" />
                <Typography>{profile?.threatAssessment?.Alert}</Typography>
              </Stack>
            )}
            {profile?.threatAssessment?.suspiciousObjects && profile?.threatAssessment?.suspiciousObjects.length > 0 && (
              <Stack direction="row" spacing={1} alignItems="center">
                <VisibilityOffIcon color="warning" />
                <Typography>{profile?.threatAssessment?.suspiciousObjects?.toString()}</Typography>
              </Stack>
            )}
            {profile?.threatAssessment?.violenceIndicators && profile?.threatAssessment?.violenceIndicators.length > 0 && (
              <Stack direction="row" spacing={1} alignItems="center">
                <ReportGmailerrorredIcon color="primary" />
                <Typography>{profile?.threatAssessment?.violenceIndicators?.toString()}</Typography>
              </Stack>
            )}
            {profile?.threatAssessment?.weapons && profile?.threatAssessment?.weapons.length > 0 && (
              <Stack direction="row" spacing={1} alignItems="center">
                <SecurityIcon color="error" />
                <Typography>{profile?.threatAssessment?.weapons?.toString()}</Typography>
              </Stack>
            )}

            {profile?.locationEvidence && (
              <Stack direction="column" spacing={1}>
                <Typography variant="h6">Location evidence:</Typography>
              </Stack>
            )}
            {isEmpty(profile?.locationEvidence) && (
              <Stack direction="column" spacing={1}>
                <Chip label={"Not detected"} size="small" />
              </Stack>
            )}
            {profile?.locationEvidence && profile?.locationEvidence?.length > 0 && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip title="Landmark">
                  <PlaceIcon color="primary" />
                </Tooltip>
                <Stack direction="column">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <OutputChip array={profile?.locationEvidence ?? []} />
                  </Stack>
                </Stack>
              </Stack>
            )}
            {profile?.locationConfidence && (
              <Stack direction="column" spacing={1}>
                <Typography variant="h6">Location confidence:</Typography>
              </Stack>
            )}
            {profile?.locationConfidence &&
              Number(profile?.locationConfidence) > 0 && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip title="Confidence">
                  
                  <MyLocationIcon color="primary" />
                </Tooltip>
                <Typography>{profile?.locationConfidence * 100}%</Typography>
              </Stack>
            )}

            {profile?.confidence && (
              <Stack direction="column" spacing={1}>
                <Typography variant="h6">Profile confidence:</Typography>
              </Stack>
            )}
            {profile?.confidence &&
              Number(profile?.confidence) > 0 && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip title="Confidence">
                  <Explore color="primary" />
                </Tooltip>
                <Typography>{profile?.confidence * 100}%</Typography>
              </Stack>
            )}
          </Stack>
        </Grid>
        <Grid item xs={3} sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start' }}>
          <Stack direction="column" spacing={1}>
            {profile?.reflections && (
              <Stack direction="column" spacing={1}>
                <Typography variant="h6">Analyzed elements:</Typography>
              </Stack>
            )}
            {isEmpty(profile?.reflections) && (
              <Stack direction="column" spacing={1}>
                <Chip label={"Not detected"} size="small" />
              </Stack>
            )}

            {profile?.reflections?.analyzedElements && profile?.reflections?.analyzedElements.length > 0 && (
              <Stack direction="row" spacing={1} alignItems="center">
                <OutputChip array={profile.reflections.analyzedElements ?? []} />
              </Stack>
            )}
            
            {profile?.Entities && (
              <Stack direction="column" spacing={1}>
                <Typography variant="h6">Entities:</Typography>
              </Stack>
            )}
            {isEmpty(profile?.Entities) && (
              <Stack direction="column" spacing={1}>
                <Chip label={"Not detected"} size="small" />
              </Stack>
            )}

            {profile?.Entities && profile?.Entities.length > 0 && (
              <Stack direction="row" spacing={1} alignItems="center">
                <OutputChip array={profile.Entities ?? []} />
              </Stack>
            )}
          </Stack>
        </Grid>
        <Grid item xs={3} sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start' }}>
          <Stack direction="column" spacing={1}>
            {profile?.textsAndSymbols && (
              <Stack direction="column" spacing={1}>
                <Typography variant="h6">Texts and symbols:</Typography>
              </Stack>
            )}

            {isEmpty(profile?.textsAndSymbols) && (
              <Stack direction="column" spacing={1}>
                <Chip label={"Not detected"} size="small" />
              </Stack>
            )}

            {profile?.textsAndSymbols?.symbolsOrLogos && profile?.textsAndSymbols?.symbolsOrLogos.length > 0 && (
              <Stack direction="row" spacing={1} alignItems="center">
                <OutputChip array={profile.textsAndSymbols.symbolsOrLogos ?? []} />
              </Stack>
            )}
            {profile?.textsAndSymbols?.uniformsOrFlags && profile?.textsAndSymbols?.uniformsOrFlags.length > 0 && (
              <Stack direction="row" spacing={1} alignItems="center">
                <OutputChip array={profile.textsAndSymbols.uniformsOrFlags ?? []} />
              </Stack>
            )}
            {profile?.textsAndSymbols?.visibleTexts && profile?.textsAndSymbols?.visibleTexts.length > 0 && (
              <Stack direction="row" spacing={1} alignItems="center">
                <OutputChip array={profile.textsAndSymbols.visibleTexts ?? []} />
              </Stack>
            )}

            {profile?.objectsAndScene && (
              <Stack direction="column" spacing={1}>
                <Typography variant="h6">Objects and scene:</Typography>
              </Stack>
            )}
            {isEmpty(profile?.objectsAndScene) && (
              <Stack direction="column" spacing={1}>
                <Chip label={"Not detected"} size="small" />
              </Stack>
            )}

            {profile?.objectsAndScene?.notableObjects && profile?.objectsAndScene?.notableObjects.length > 0 && (
              <Stack direction="row" spacing={1} alignItems="center">
                <OutputChip array={profile.objectsAndScene.notableObjects ?? []} />
              </Stack>
            )}

            {profile?.objectsAndScene?.vehiclesOrTech && profile?.objectsAndScene?.vehiclesOrTech.length > 0 && (
              <Stack direction="row" spacing={1} alignItems="center">
                <OutputChip array={profile.objectsAndScene.vehiclesOrTech} />
              </Stack>
            )}

            {profile?.vericityScore && (
              <Stack direction="column" spacing={1}>
                <Typography variant="h6">Vericity Score:</Typography>
              </Stack>
            )}
            {isEmpty(profile?.vericityScore) && (
              <Stack direction="column" spacing={1}>
                <Chip label={"Not detected"} size="small" />
              </Stack>
            )}

            {isNumber(profile?.vericityScore?.location) && profile?.vericityScore?.location > 0 && (
              <Stack direction="row" alignItems="center">
                <Tooltip title="Location vericity score">
                <LocationCityIcon color="primary" />
                </Tooltip>
                <Typography>{`${profile?.vericityScore.location}%`}</Typography>
              </Stack>
            )}

            {isNumber(profile?.vericityScore?.time) && profile?.vericityScore?.time > 0 && (
              <Stack direction="row" alignItems="center">
                <Tooltip title="Time vericity score">
                <AccessTimeIcon color="primary" />
                </Tooltip>
                <Typography>{`${profile?.vericityScore.time}%`}</Typography>
              </Stack>
            )}

            {isNumber(profile?.vericityScore?.people) && profile?.vericityScore?.people > 0 && (
              <Stack direction="row" alignItems="center">
                <Tooltip title="People vericity score">
                <PeopleIcon color="primary" />
                </Tooltip>
                <Typography>{`${profile?.vericityScore.people}%`}</Typography>
              </Stack>
            )}

            {isNumber(profile?.vericityScore?.textsAndSymbols) && profile?.vericityScore?.textsAndSymbols > 0 && (
              <Stack direction="row" alignItems="center">
                <Tooltip title="Texts and symbols vericity score">
                <TextFieldsIcon color="primary" />
                </Tooltip>
                <Typography>{`${profile?.vericityScore.textsAndSymbols}%`}</Typography>
              </Stack>
            )}

            {isNumber(profile?.vericityScore?.threatAssessment) && profile?.vericityScore?.threatAssessment > 0 && (
              <Stack direction="row" alignItems="center">
                <Tooltip title="Threat assessment vericity score">
                <WarningAmberIcon color="primary" />
                </Tooltip>
                <Typography>{`${profile?.vericityScore.threatAssessment}%`}</Typography>
              </Stack>
            )}

            {isNumber(profile?.vericityScore?.objectsAndScene) && profile?.vericityScore?.objectsAndScene > 0 && (
              <Stack direction="row" alignItems="center">
                <Tooltip title="Objects and scene vericity score">
                <PhotoCameraIcon color="primary" />
                </Tooltip>
                <Typography>{`${profile?.vericityScore.objectsAndScene}%`}</Typography>
              </Stack>
            )}

            {isNumber(profile?.vericityScore?.reflections) && profile?.vericityScore?.reflections > 0 && (
              <Stack direction="row" alignItems="center">
                <Tooltip title="Reflections vericity score">
                <FlipCameraAndroidIcon color="primary" />
                </Tooltip>
                <Typography>{`${profile?.vericityScore.reflections}%`}</Typography>
              </Stack>
            )}
          </Stack>
        </Grid>
       <Grid item sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start' }}>
            {profile?.summary && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip title="Summary">
                  <InfoOutlined color="primary" />
                </Tooltip>
                <Typography>{profile?.summary}</Typography>
              </Stack>
            )}
       </Grid>
      </Grid>
    </>
  )
}
